import { Exclude, Expose } from 'class-transformer';
import { BaseModel } from '../models';
import {
  ADDRESS_SUB_TYPE,
  CBS_MAX_DELQ_L6M,
  CBS_RISK_GRADE,
  EKeyPersonnelAttachmentKey,
  EKeyPersonnelAttachmentSection,
  EKeyPersonnelTag,
  GENDER,
  KEY_PERSONNEL_ADDRESS_TYPE,
  KEY_PERSONNEL_APPOINTMENT,
  KEY_PERSONNEL_MARITAL_STATUS,
  KEY_PERSONNEL_METADATA,
  KEY_PERSONNEL_OWNERSHIP,
  KEY_PERSONNEL_RACE,
  KEY_PERSONNEL_RELATIONSHIP_TO_APPLICANT,
  KEY_PERSONNEL_RESIDENCIAL_STATUS,
  KEY_PERSONNEL_TYPE_OF_RESIDENCE,
} from '../types';

@Exclude()
export class CaseKeyPersonnelFatcaDto extends BaseModel {
  @Expose()
  usPermanentResidentCardHolder!: boolean;

  @Expose()
  usPermanentResidentCardHolderTaxNumber!: string;

  @Expose()
  visa!: string;
}

@Exclude()
export class CaseKeyPersonnelMetadataDto extends BaseModel implements KEY_PERSONNEL_METADATA {
  @Expose()
  tags!: EKeyPersonnelTag[];

  @Expose()
  uuid!: string;
}

@Exclude()
export class AggregatedOutstandingBalanceDto extends BaseModel {
  @Expose()
  date!: string;

  @Expose()
  securedBalances!: number;

  @Expose()
  unsecuredInterestBearingBalances!: number;

  @Expose()
  unsecuredNonInterestBearingBalances!: number;

  @Expose()
  exemptedBalances!: number;
}

@Exclude()
export class CreditBureauSingaporeDto extends BaseModel {
  @Expose()
  reportDate!: Date;

  @Expose()
  enquiryNumber!: string;

  @Expose()
  dateOfEarliestKnownCreditAccount!: string;

  @Expose()
  previousEnquiries!: string;

  @Expose()
  accounts!: string;

  @Expose()
  defaults!: string;

  @Expose()
  bankruptcyProceedings!: number;

  @Expose()
  securedCreditLimit!: number;

  @Expose()
  unsecuredCreditLimit!: number;

  @Expose()
  exemptedCreditLimit!: number;

  @Expose()
  riskGrade!: CBS_RISK_GRADE | string;

  @Expose()
  riskGradeDescription!: string;

  @Expose()
  maxDelqL6M!: CBS_MAX_DELQ_L6M;

  @Expose()
  aggregatedMonthlyInstalment!: number;

  @Expose()
  creditReportRequest!: boolean;

  @Expose()
  aggregatedOutstandingBalances!: AggregatedOutstandingBalanceDto[];
}

@Exclude()
export class KeyPersonnelCRSDto extends BaseModel {
  @Expose()
  id!: number;

  @Expose()
  countryId!: number;

  @Expose()
  isHaveTIN!: boolean;

  @Expose()
  tin!: string;
}

@Exclude()
export class KeyPersonnelAddressAttr extends BaseModel {
  @Expose()
  differentResidential!: boolean;
}

export class KeyPersonnelAddressDto extends BaseModel {
  @Expose()
  id!: number;

  @Expose()
  subType!: ADDRESS_SUB_TYPE;

  @Expose()
  postalCode!: string;

  @Expose()
  blockNumber!: string;

  @Expose()
  streetName!: string;

  @Expose()
  storey!: string;

  @Expose()
  unitNumber!: string;

  @Expose()
  buildingName!: string;

  @Expose()
  addressLine1!: string;

  @Expose()
  addressLine2!: string;

  @Expose()
  countryId!: number;

  @Expose()
  type!: KEY_PERSONNEL_ADDRESS_TYPE;

  @Expose()
  attrs!: KeyPersonnelAddressAttr;
}

@Exclude()
export class KeyPersonnelAttachmentAttr extends BaseModel {
  @Expose()
  section!: EKeyPersonnelAttachmentSection;

  @Expose()
  key!: EKeyPersonnelAttachmentKey;
}

@Exclude()
export class KeyPersonnelAttachmentDto extends BaseModel {
  @Expose()
  id!: number;

  @Expose()
  drive!: string;

  @Expose()
  name!: string;

  @Expose()
  contentType!: string;

  @Expose()
  keyPath!: string;

  @Expose()
  attrs!: KeyPersonnelAttachmentAttr;
}

@Exclude()
export class CaseKeyPersonnelDto extends CaseKeyPersonnelFatcaDto {
  @Expose()
  id!: number;

  @Expose()
  name!: string;

  @Expose()
  nricNumber!: string;

  @Expose()
  email!: string;

  @Expose()
  contactAreaCode!: string;

  @Expose()
  contactNumber!: string;

  @Expose()
  appointment!: KEY_PERSONNEL_APPOINTMENT[];

  @Expose()
  share!: number;

  @Expose()
  nationalityId!: number;

  @Expose()
  residentialStatus!: KEY_PERSONNEL_RESIDENCIAL_STATUS;

  @Expose()
  residencyId!: number;

  @Expose()
  dateOfBirthOnly!: string;

  @Expose()
  placeOfBirthCountryId!: number;

  @Expose()
  placeOfBirthProvinceId!: number;

  @Expose()
  placeOfBirthCityId!: number;

  @Expose()
  gender!: GENDER;

  @Expose()
  relationshipToApplicant!: KEY_PERSONNEL_RELATIONSHIP_TO_APPLICANT[];

  @Expose()
  relationshipToApplicantOther!: string;

  @Expose()
  typeOfResidence!: KEY_PERSONNEL_TYPE_OF_RESIDENCE;

  @Expose()
  lengthOfStay!: number;

  @Expose()
  residentialAreaCode!: string;

  @Expose()
  residentialNumber!: string;

  @Expose()
  officeAreaCode!: string;

  @Expose()
  officeNumber!: string;

  @Expose()
  race!: KEY_PERSONNEL_RACE;

  @Expose()
  raceOther!: string;

  @Expose()
  yearsOfExperience!: number;

  @Expose()
  dateOfAppointmentOnly!: Date;

  @Expose()
  ownership!: KEY_PERSONNEL_OWNERSHIP;

  @Expose()
  ownershipOther!: string;

  @Expose()
  rentalAmount!: number;

  @Expose()
  usResident!: boolean;

  @Expose()
  usResidentTaxNumber!: string;

  @Expose()
  usCitizen!: boolean;

  @Expose()
  usCitizenTaxNumber!: string;

  @Expose()
  annualIncomeLatestYear!: number;

  @Expose()
  annualIncome!: number;

  @Expose()
  tradeIncomeLatestYear!: number;

  @Expose()
  tradeIncome!: number;

  @Expose()
  creditBureauSingapore!: CreditBureauSingaporeDto;

  @Expose()
  crs!: KeyPersonnelCRSDto[];

  @Expose()
  isContactEmail!: boolean;

  @Expose()
  isContactSMS!: boolean;

  @Expose()
  isEAdvice!: boolean;

  @Expose()
  maritalStatus!: KEY_PERSONNEL_MARITAL_STATUS;

  @Expose()
  remark!: string;

  @Expose()
  metadata!: CaseKeyPersonnelMetadataDto;

  @Expose()
  attachments!: KeyPersonnelAttachmentDto[];

  @Expose()
  addresses!: KeyPersonnelAddressDto[];

  @Expose()
  confirmationStatus!: 'pending' | 'processing' | 'confirmed';

  @Expose()
  dualNationality!: boolean;
}
